import React, { useState, useEffect, useRef } from 'react';
import { TransactionDetail, InvoiceLineItem } from '../../types/SalesReport';
import { customerSalesComparisonService } from '../../services/customerSalesComparisonService';
import { formatDate } from '../../utils/date';

interface InvoiceDetailDialogProps {
  invoice: TransactionDetail | null;
  onClose: () => void;
}

const InvoiceDetailDialog: React.FC<InvoiceDetailDialogProps> = ({ invoice, onClose }) => {
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [lineItemsError, setLineItemsError] = useState<string | null>(null);
  const isFetchingRef = useRef<string | null>(null);

  useEffect(() => {
    if (!invoice) {
      setLineItems([]);
      setLineItemsError(null);
      isFetchingRef.current = null;
      return;
    }

    const documentNumber = invoice.document_number;

    // Prevent duplicate calls for the same document
    if (isFetchingRef.current === documentNumber) {
      return;
    }

    // Set flag IMMEDIATELY before async call to prevent StrictMode duplicate
    isFetchingRef.current = documentNumber;

    setLoadingLineItems(true);
    setLineItemsError(null);

    customerSalesComparisonService
      .getInvoiceLineItems(documentNumber)
      .then((response) => {
        // Only process if this is still the current request
        if (isFetchingRef.current !== documentNumber) {
          return;
        }

        if (response.success) {
          setLineItems(response.data || []);
        } else {
          setLineItemsError('Failed to load line items');
        }
      })
      .catch((error) => {
        // Only show error if this is still the current request
        if (isFetchingRef.current !== documentNumber) {
          return;
        }
        console.error('Error loading invoice line items:', error);
        setLineItemsError(error.message || 'Failed to load line items');
      })
      .finally(() => {
        // Only clear flag if this is still the current request
        if (isFetchingRef.current === documentNumber) {
          setLoadingLineItems(false);
          isFetchingRef.current = null;
        }
      });

    // Don't clear the flag in cleanup - let the finally block handle it
    // This prevents StrictMode from clearing the flag before the second run
    return () => {
      // Only clear if document number changed (not on StrictMode cleanup)
      // The finally block will clear it when the request completes
    };
  }, [invoice?.document_number]);

  if (!invoice) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{
          maxWidth: '900px',
          padding: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Invoice Details</h3>
          <button onClick={onClose} aria-label="Close dialog">
            ×
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* First Line: Document Number, Date, Type, Status */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 2fr 1.5fr',
              gap: '1.5rem',
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              alignItems: 'end',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Document Number
              </label>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  fontFamily: 'monospace',
                }}
              >
                {invoice.document_number}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Document Date
              </label>
              <div style={{ fontSize: '1rem', color: '#111827' }}>
                {formatDate(invoice.document_date)}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Document Type
              </label>
              <div>
                <span
                  className={`badge ${
                    invoice.document_type.includes('Invoice') ||
                    invoice.document_type.includes('Debit')
                      ? 'badge-green'
                      : 'badge-red'
                  }`}
                >
                  {invoice.document_type}
                </span>
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Status
              </label>
              <div>
                <span
                  className={`badge ${
                    invoice.transaction_status === 'posted' ? 'badge-green' : 'badge-yellow'
                  }`}
                >
                  {invoice.transaction_status === 'posted' ? 'Posted' : 'Unposted'}
                </span>
              </div>
            </div>
          </div>

          {/* Second Line: Customer Number, Customer Name, Net Sales Amount */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 3fr 2fr',
              gap: '1.5rem',
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              alignItems: 'end',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Customer Number
              </label>
              <div style={{ fontSize: '1rem', color: '#111827', fontFamily: 'monospace' }}>
                {invoice.customer_number}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Customer Name
              </label>
              <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 500 }}>
                {invoice.customer_name}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Net Sales Amount
              </label>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: invoice.net_sales >= 0 ? '#059669' : '#dc2626',
                }}
              >
                {formatCurrency(invoice.net_sales)}
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div>
            <h4
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '1rem',
              }}
            >
              Invoice Line Items
            </h4>
            {loadingLineItems ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
                <span style={{ marginTop: '0.5rem', display: 'block', color: '#6b7280' }}>
                  Loading line items...
                </span>
              </div>
            ) : lineItemsError ? (
              <div
                style={{
                  padding: '1rem',
                  backgroundColor: '#fef2f2',
                  borderRadius: '4px',
                  color: '#dc2626',
                }}
              >
                {lineItemsError}
              </div>
            ) : lineItems && lineItems.length > 0 ? (
              <div className="table-scroll" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead className="thead">
                    <tr>
                      <th className="th">Product Code</th>
                      <th className="th">Description</th>
                      <th className="th text-right">Quantity</th>
                      <th className="th text-right">Unit Price</th>
                      <th className="th text-right">Extended Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={index} className="tr">
                        <td className="td font-mono text-sm">{item.item_number}</td>
                        <td className="td">
                          <div>
                            <div style={{ fontWeight: 500 }}>{item.item_description}</div>
                            {item.product_name && (
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  marginTop: '0.25rem',
                                }}
                              >
                                {item.product_name}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="td text-right">{item.quantity.toLocaleString()}</td>
                        <td className="td text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="td text-right" style={{ fontWeight: 600 }}>
                          {formatCurrency(item.extended_price)}
                        </td>
                      </tr>
                    ))}
                    <tr className="tr" style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}>
                      <td className="td" colSpan={4} style={{ textAlign: 'right' }}>
                        Total:
                      </td>
                      <td className="td text-right">
                        {formatCurrency(
                          lineItems.reduce((sum, item) => sum + item.extended_price, 0)
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                No line items found for this invoice.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailDialog;
